import { useRef, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { addDays } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import api from 'api';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import {
  DatepickerWithTime,
  Form,
  FormGroup,
  RadioGroup
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { ServerConfig } from 'shared-types';
import type { PageState } from 'types';

type FormValues = ReturnType<typeof getInitialValues>;

const getInitialValues = (
  path: PageState['path'],
  serverConfig: ServerConfig<'guest'>,
  dateNow: number
) => {
  const published = !path.startDate
    ? 'no'
    : Date.parse(path.startDate) > dateNow
    ? 'later'
    : Date.parse(path.endDate) < dateNow
    ? 'ended'
    : 'yes';

  return {
    endDate: path.endDate
      ? Date.parse(toZonedTime(path.endDate, serverConfig.timeZone).toString())
      : null,
    published,
    startDate: path.startDate
      ? Date.parse(
          toZonedTime(path.startDate, serverConfig.timeZone).toString()
        )
      : null
  };
};

const PathPublish = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path },
    serverConfig,
    theme
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);
  const initialValues = useRef(getInitialValues(path, serverConfig, dateNow));

  const [loading, setLoading] = useState(false);

  const handlePublishedChange = (value: 'ended' | 'later' | 'no' | 'yes') => {
    const { startDate, endDate } = initialValues.current;

    if (value === 'no') {
      formApi.current.setValue('startDate', null);
      formApi.current.setValue('endDate', null);
    } else if (value === 'yes') {
      if (startDate && startDate <= Date.now())
        formApi.current.setValue('startDate', startDate);
      else formApi.current.setValue('startDate', null);

      if (endDate && endDate < Date.now())
        formApi.current.setValue('endDate', null);
    } else if (value === 'later') {
      if (startDate && startDate > Date.now())
        formApi.current.setValue('startDate', startDate);
      else
        formApi.current.setValue(
          'startDate',
          Date.parse(addDays(Date.now(), 1).toString())
        );

      if (endDate && endDate < Date.now())
        formApi.current.setValue('endDate', null);
    } else if (value === 'ended') {
      formApi.current.setValue('startDate', startDate);

      if (endDate && endDate < Date.now())
        formApi.current.setValue('endDate', endDate);
      else formApi.current.setValue('endDate', Date.now());
    }
  };

  const submit = async (values: FormValues) => {
    setLoading(true);

    try {
      let startDate: number = values.startDate || null;
      if (values.published === 'yes' && !startDate) {
        startDate = Date.now();
      } else if (
        values.published === 'later' &&
        startDate !== initialValues.current.startDate
      ) {
        startDate = Date.parse(
          toDate(startDate, { timeZone: serverConfig.timeZone }).toString()
        );
      }

      let endDate: number = values.endDate || null;
      if (
        values.published === 'ended' &&
        values.endDate !== initialValues.current.endDate
      ) {
        endDate = Date.now();
      } else if (endDate && endDate !== initialValues.current.endDate) {
        endDate = Date.parse(
          toDate(endDate, { timeZone: serverConfig.timeZone }).toString()
        );
      }

      await api.put(`/admin/paths/${path.id}`, { startDate, endDate });

      const paths = await api.get(`/admin/paths?id=${path.id}`);
      dispatch.updatePageState({ path: paths.data.paths[0] });

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });

      initialValues.current = getInitialValues(
        paths.data.paths[0],
        serverConfig,
        Date.now()
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings">
      <Box title={messages.commonPublish}>
        <Form
          getApi={(form) => (formApi.current = form)}
          initialValues={initialValues.current}
          onSubmit={submit}
        >
          {({ formState }) => (
            <div>
              <FormGroup label={messages.publishPublishPath}>
                <RadioGroup
                  field="published"
                  onChange={handlePublishedChange}
                  options={[
                    {
                      id: 'published-no',
                      label:
                        initialValues.current.published === 'no'
                          ? messages.commonNotPublished
                          : messages.commonUnpublish,
                      value: 'no'
                    },
                    {
                      id: 'published-yes',
                      label:
                        initialValues.current.published === 'yes'
                          ? messages.commonPublished
                          : messages.publishPublishNow,
                      value: 'yes'
                    },
                    {
                      id: 'published-later',
                      label:
                        initialValues.current.published === 'later'
                          ? messages.publishWillPublish
                          : messages.publishPublishLater,
                      value: 'later'
                    },
                    ...(initialValues.current.published === 'yes' ||
                    initialValues.current.published === 'ended'
                      ? [
                          {
                            id: 'published-ended',
                            label:
                              initialValues.current.published === 'ended'
                                ? messages.publishEnded
                                : messages.publishPathEndNow,
                            tooltip: messages.publishPathEndedInfo,
                            value: 'ended'
                          }
                        ]
                      : [])
                  ]}
                />
              </FormGroup>

              <div style={{ marginTop: 10 }}>
                <div className="row-sm">
                  <div className="col-min">
                    <FormGroup
                      htmlFor="start-date"
                      label={messages.publishStartDate}
                    >
                      <DatepickerWithTime
                        disabled={formState.values.published !== 'later'}
                        field="startDate"
                        id="start-date"
                        isClearable={false}
                        minDate={addDays(dateNow, 1)}
                        placeholder={
                          formState.values.published === 'no'
                            ? messages.publishStartDateEmpty
                            : messages.publishNow
                        }
                        style={{ width: 200 }}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-min col-no-top-margin">
                    <FormGroup
                      htmlFor="end-date"
                      label={messages.publishEndDate}
                    >
                      <DatepickerWithTime
                        disabled={
                          formState.values.published === 'no' ||
                          formState.values.published === 'ended'
                        }
                        field="endDate"
                        id="end-date"
                        isClearable={true}
                        minDate={dateNow}
                        placeholder={
                          formState.values.published === 'ended'
                            ? messages.publishNow
                            : messages.publishEndDateEmpty
                        }
                        style={{ width: 200 }}
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <FormGroup style={{ marginTop: 25 }}>
                <Button
                  category={
                    initialValues.current.published ===
                      formState.values.published ||
                    formState.values.published === 'yes' ||
                    formState.values.published === 'later'
                      ? 'success'
                      : 'danger'
                  }
                  disabled={formState.pristine}
                  loading={loading}
                  text={
                    initialValues.current.published ===
                    formState.values.published
                      ? messages.commonSaveChanges
                      : formState.values.published === 'no'
                      ? messages.publishUnpublishPath
                      : formState.values.published === 'yes'
                      ? messages.publishPublishPath
                      : formState.values.published === 'later'
                      ? messages.publishSetPublishDate
                      : messages.publishEndPath
                  }
                  type="submit"
                />
              </FormGroup>
            </div>
          )}
        </Form>
      </Box>

      <style jsx>{`
        .chapters {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
        }
        .chapters-header {
          align-items: center;
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
          color: ${theme.textColorLight};
          display: flex;
          flex-wrap: wrap;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 10px;
          justify-content: space-between;
          background-color: ${theme.lightGrey};
          padding: 10px 15px;
        }
        .chapter {
          padding: 10px 15px;
        }
        .chapter:not(:first-child) {
          border-top: 1px solid white;
        }
        .chapter-title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
        }
        .chapter-publish {
          align-items: flex-end;
          display: flex;
          flex-wrap: wrap;
        }
        .chapter-publish-options {
          margin-right: 10px;
          padding-bottom: 7px;
        }
        .chapters-warning {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          padding: 10px 15px;
        }
      `}</style>
    </div>
  );
};

export default PathPublish;
